import Editor from "@monaco-editor/react";
import { Description as DescriptionIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import CustomSnackbar from "../../components/CustomSnackbar";
import Item from "../../components/Item";
import StripeService from "./StripeService";
import Logger from "../../../services/Logger";
import checkoutSessionComplete from "./checkout_session_complete.json";
import invoicePaymentSucceeded from "./invoice.payment_succeeded.json";
import invoicePaymentPaid from "./invoice_payment_paid.json";

export default function StripeMain() {
    const [jsonData, setJsonData] = useState("");
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [hasError, setHasError] = useState(false);
    const [editorHeight, setEditorHeight] = useState(600);
    const editorContainerRef = useRef(null);
    const rafRef = useRef(null);
    const lastHeightRef = useRef(600);

    useEffect(() => {
        const updateEditorHeight = () => {
            if (editorContainerRef.current) {
                const containerHeight = editorContainerRef.current.clientHeight;
                const viewportHeight = window.innerHeight;
                // Limit height to viewport height minus some padding (e.g., 200px for header/nav/other UI)
                const maxHeight = viewportHeight - 200;
                const height = Math.min(containerHeight, maxHeight);
                
                if (height > 0 && height !== lastHeightRef.current) {
                    lastHeightRef.current = height;
                    setEditorHeight(height);
                }
            }
        };

        const debouncedUpdate = () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(() => {
                updateEditorHeight();
            });
        };

        // Initial update
        updateEditorHeight();

        const resizeObserver = new ResizeObserver(debouncedUpdate);
        const windowResizeHandler = debouncedUpdate;

        if (editorContainerRef.current) {
            resizeObserver.observe(editorContainerRef.current);
        }
        
        window.addEventListener('resize', windowResizeHandler);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            if (editorContainerRef.current) {
                resizeObserver.unobserve(editorContainerRef.current);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', windowResizeHandler);
        };
    }, []);

    const handleEditorChange = (value) => {
        setJsonData(value || "");
        setHasError(false);
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleLoadFile = (fileContent, fileName) => {
        try {
            const formattedJson = JSON.stringify(fileContent, null, 2);
            setJsonData(formattedJson);
            setHasError(false);
            showSnackbar(`Loaded ${fileName}`, "success");
            
            // Update editor height after a short delay to let layout settle
            setTimeout(() => {
                if (editorContainerRef.current) {
                    const containerHeight = editorContainerRef.current.clientHeight;
                    const viewportHeight = window.innerHeight;
                    const maxHeight = viewportHeight - 200;
                    const height = Math.min(containerHeight, maxHeight);
                    
                    if (height > 0 && height !== lastHeightRef.current) {
                        lastHeightRef.current = height;
                        setEditorHeight(height);
                    }
                }
            }, 100);
        } catch (err) {
            showSnackbar("Failed to load file: " + err.message, "error");
            setHasError(true);
        }
    };

    const handleSend = async () => {
        if (!jsonData.trim()) {
            showSnackbar("Please enter JSON data", "error");
            setHasError(true);
            return;
        }

        setLoading(true);
        setHasError(false);

        try {
            // Parse JSON to validate it
            let parsedData;
            try {
                parsedData = JSON.parse(jsonData);
            } catch (parseError) {
                showSnackbar("Invalid JSON format: " + parseError.message, "error");
                setHasError(true);
                setLoading(false);
                return;
            }

            // Send to server
            const response = await StripeService.sendTestWebhook(parsedData);
            
            if (response && response.code === "FAILURE") {
                showSnackbar(response.message || "Failed to send webhook", "error");
                setHasError(true);
            } else {
                showSnackbar("Webhook sent successfully!", "success");
                Logger.log("Stripe webhook response:", response);
            }
        } catch (err) {
            showSnackbar("Failed to send webhook: " + (err.message || "Unknown error"), "error");
            setHasError(true);
            Logger.error("Stripe webhook error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Item sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Stripe Webhook Test
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Enter a Stripe webhook event JSON and send it to the test endpoint.
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                <Tooltip title="Load checkout_session_complete.json">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleLoadFile(checkoutSessionComplete, "checkout_session_complete.json")}
                                        color="primary"
                                    >
                                        <DescriptionIcon />
                                    </IconButton>                                
                                </Tooltip>
                                <Tooltip title="Load invoice.payment_succeeded.json">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleLoadFile(invoicePaymentSucceeded, "invoice.payment_succeeded.json")}
                                        color="primary"
                                    >
                                        <DescriptionIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Load invoice_payment_paid.json">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleLoadFile(invoicePaymentPaid, "invoice_payment_paid.json")}
                                        color="primary"
                                    >
                                        <DescriptionIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={loading}
                                    size="small"
                                >
                                    {loading ? (
                                        <>
                                            <CircularProgress size={16} sx={{ mr: 1 }} />
                                            Sending...
                                        </>
                                    ) : (
                                        "Send Webhook"
                                    )}
                                </Button>
                            </Box>
                        </Box>

                        <Box 
                            ref={editorContainerRef}
                            sx={{ 
                                border: hasError ? '1px solid #d32f2f' : '1px solid #e0e0e0',
                                borderRadius: 1,
                                overflow: 'hidden',
                                flex: 1,
                                minHeight: 0,
                                maxHeight: 'calc(100vh - 200px)'
                            }}
                        >
                            <Editor
                                height={editorHeight}
                                defaultLanguage="json"
                                value={jsonData}
                                onChange={handleEditorChange}
                                theme="vs-light"
                                options={{
                                    minimap: { enabled: false },
                                    scrollBeyondLastLine: false,
                                    fontSize: 14,
                                    wordWrap: "on",
                                    formatOnPaste: true,
                                    formatOnType: true,
                                    tabSize: 2,
                                    tabFocusMode: false  // Tab inserts indent
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Item>
            
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
}

